<template>
  <div>
    <LevelComplete v-if="gameOver" />
    <div
      v-if="!gameOver"
      class="fruit-game"
    >
      <!-- Basket target -->
      <div
        class="basket-target"
        @dragover.prevent
        @drop="handleDrop($event)"
      >
        <h2 class="basket-label">
          Basket
        </h2>
        <!-- Display the correctly dropped items -->
        <div class="dropped-items">
          <span
            v-for="(item, index) in correctDrops"
            :key="index"
          >
            {{ item.emoji }}
          </span>
        </div>
      </div>

      <!-- Current instruction -->
      <p class="instruction">
        {{ currentInstruction }}
      </p>

      <!-- Feedback message -->
      <p
        v-if="message"
        class="message"
      >
        {{ message }}
      </p>

      <!-- Draggable fruits -->
      <div class="draggable-items">
        <div
          v-for="(item) in draggableItems"
          :key="item.id"
          class="draggable-emoji"
          :style="draggedItem === item ? draggedStyle : {}"
          draggable="true"
          @dragstart="handleDrag($event, item)"
          @click="speakFruit(item.name)"
          @touchstart="handleTouchStart($event, item)"
          @touchmove="handleTouchMove($event)"
          @touchend="handleTouchEnd($event)"
        >
          {{ item.emoji }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LevelComplete from "../LevelComplete.vue";
import { speechMixin } from "@/mixins/speechMixin.js";

export default {
  components: {
    LevelComplete,
  },
  mixins: [speechMixin],
  data() {
    const tasks = [
      { name: "Strawberry", emoji: "🍓", quantity: 4 },
      { name: "Banana", emoji: "🍌", quantity: 5 },
      { name: "Apple", emoji: "🍎", quantity: 3 },
      { name: "Grape", emoji: "🍇", quantity: 6 },
      { name: "Carrot", emoji: "🥕", quantity: 2 },
    ];

    return {
      tasks,
      currentTaskIndex: 0,
      draggableItems: [],
      correctDrops: [],
      message: "",
      draggedItem: null,
      draggedStyle: {},
      selectedVoice: null,
      gameOver: false,
    };
  },
  computed: {
    currentTask() {
      if (this.gameOver) return null;
      return this.tasks[this.currentTaskIndex];
    },
    currentInstruction() {
      if (this.gameOver) return "";
      const fruitName = this.currentTask.name;
      const quantity = this.currentTask.quantity;
      return `Drop ${quantity} ${fruitName}${
        quantity > 1 ? "s" : ""
      } into the basket.`;
    },
  },
  mounted() {
    // Initialize draggable items for the first task
    this.initializeDraggableItems();

    // Voice setup
    if (typeof speechSynthesis === "undefined") {
      console.warn("Speech Synthesis API not supported in this browser.");
    } else {
      speechSynthesis.onvoiceschanged = () => this.setupVoices();
    }
    this.setupVoices();
  },
  methods: {
    initializeDraggableItems() {
      const currentTask = this.currentTask;

      // Generate the correct number of the required fruit
      this.draggableItems = [];
      let idCounter = 1;

      // Add the required fruits
      for (let i = 0; i <= currentTask.quantity; i++) {
        this.draggableItems.push({
          id: idCounter++,
          emoji: currentTask.emoji,
          name: currentTask.name,
          isTarget: true,
        });
      }

      // Add some distractor fruits
      const otherTasks = this.tasks.filter(
        (task, index) => index !== this.currentTaskIndex
      );

      // Add some distractor fruits randomly
      otherTasks.forEach((task) => {
        const numberOfItems = Math.floor(Math.random() * 3) + 1; // 1 to 3 distractor items per fruit
        for (let i = 0; i < numberOfItems; i++) {
          this.draggableItems.push({
            id: idCounter++,
            emoji: task.emoji,
            name: task.name,
            isTarget: false,
          });
        }
      });

      // Shuffle the draggableItems
      this.draggableItems.sort(() => Math.random() - 0.5);

      // Reset correctDrops
      this.correctDrops = [];
    },
    setupVoices() {
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        this.selectedVoice = voices[0];
      } else {
        // Fallback or handle the case when voices are not loaded yet
        setTimeout(this.setupVoices, 100);
      }
    },
    speakFruit(name, voiceName = null) {
      const utterance = new SpeechSynthesisUtterance(name);
      utterance.pitch = 1.2;
      utterance.rate = 0.7;
      const voices = window.speechSynthesis.getVoices();
      if (voiceName) {
        const selectedVoice = voices.find((voice) => voice.name === voiceName);
        if (selectedVoice) {
          utterance.voice = selectedVoice;
        }
      } else if (this.selectedVoice) {
        utterance.voice = this.selectedVoice;
      }
      window.speechSynthesis.speak(utterance);
    },
    handleDrag(event, item) {
      event.dataTransfer.setData("text/plain", JSON.stringify(item));
    },
    handleDrop(event) {
      const itemData = event.dataTransfer.getData("text/plain");
      const item = JSON.parse(itemData);

      if (item.isTarget) {
        // Correct fruit
        if (!this.correctDrops.some((i) => i.id === item.id)) {
          this.correctDrops.push(item);
          // Remove the item from draggableItems
          this.draggableItems = this.draggableItems.filter(
            (i) => i.id !== item.id
          );
          // Speak the fruit's name
          this.speakFruit(item.name);
          // Check if the task is complete
          if (this.correctDrops.length === this.currentTask.quantity) {
            // Task completed
            this.currentTaskIndex++;
            if (this.currentTaskIndex >= this.tasks.length) {
              // All tasks completed
              this.gameOver = true;
              this.speakFruit("Great Job!");

              setTimeout(() => {
                this.$router.push("/");
              }, 4000);
            } else {
              // Move to next task
              this.initializeDraggableItems();
            }
          }
        } else {
          this.message = "Already placed!";
        }
      } else {
        // Incorrect fruit
        this.message = "❌ Wrong fruit! Try again!";
        this.speakFruit("Wrong fruit! Try again!");
      }

      // Clear message after 2 seconds
      setTimeout(() => {
        this.message = "";
      }, 2000);
    },
    handleTouchStart(event, item) {
      event.preventDefault();
      const touch = event.touches[0];

      this.draggedItem = item;
      this.draggedStyle = {
        position: "fixed",
        left: `${touch.clientX}px`,
        top: `${touch.clientY}px`,
        zIndex: 1000,
        pointerEvents: "none",
        transform: "translate(-50%, -50%)",
        fontSize: "5rem",
      };
    },
    handleTouchMove(event) {
      event.preventDefault(); // Prevent scrolling
      const touch = event.touches[0];
      this.draggedStyle.left = `${touch.clientX}px`;
      this.draggedStyle.top = `${touch.clientY}px`;
    },
    handleTouchEnd(event) {
      const dropZone = this.$el.querySelector(".basket-target");
      const touchEndX = event.changedTouches[0].clientX;
      const touchEndY = event.changedTouches[0].clientY;
      let dropped = false;

      if (dropZone) {
        const rect = dropZone.getBoundingClientRect();
        if (
          touchEndX >= rect.left &&
          touchEndX <= rect.right &&
          touchEndY >= rect.top &&
          touchEndY <= rect.bottom
        ) {
          this.handleDrop({
            dataTransfer: { getData: () => JSON.stringify(this.draggedItem) },
          });
          // Remove the item from draggableItems
          this.draggableItems = this.draggableItems.filter(
            (i) => i.id !== this.draggedItem.id
          );
          dropped = true;
        }
      }

      if (!dropped) {
        this.message = "❌ Try again!";
        setTimeout(() => {
          this.message = "";
        }, 2000);
      }

      this.draggedItem = null;
      this.draggedStyle = {};
    },
  },
};
</script>

<style scoped>
.fruit-game {
  position: relative;
  width: 100%;
  min-height: 100vh;
}

/* Basket target */
.basket-target {
  width: 80%;
  height: 200px;
  border: 2px dashed #ccc;
  border-radius: 10px;
  margin: 20px auto;
  position: relative;
  text-align: center;
  padding-top: 10px;
  cursor: pointer;
}

.basket-target .basket-label {
  position: absolute;
  width: 100%;
  text-align: center;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 2rem;
  font-weight: bold;
}

.dropped-items {
  position: absolute;
  top: 50px;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: auto;
  padding: 5px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-content: start;
}

.dropped-items span {
  font-size: 3rem;
  margin: 5px;
}

/* Messages */
.instruction {
  text-align: center;
  font-size: 1.8rem;
  font-weight: bold;
}

.message {
  text-align: center;
  font-size: 2rem;
  font-weight: bold;
  color: red;
}

/* Draggable items */
.draggable-items {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: auto;
  width: 80%;
}

.draggable-emoji {
  cursor: grab;
  font-size: 5rem;
  margin: 1rem;
  transition: transform 0.3s;

  @media (max-width: 768px) {
    font-size: 3rem;
  }
}

.draggable-emoji[style*="position: fixed;"] {
  position: fixed !important;
}

.draggable-emoji:hover {
  transform: scale(1.2);
}

@media (hover: none) {
  .draggable-emoji:hover {
    transform: none;
  }
}
</style>
